
import React, { useState, useEffect, useMemo } from 'react';
import { motion, useSpring, useMotionValue } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const CustomCursor: React.FC = () => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  // Optimized spring for snappier response without losing smoothness
  const springConfig = useMemo(() => ({ stiffness: 400, damping: 35, mass: 0.5 }), []);
  const cursorX = useSpring(mouseX, springConfig);
  const cursorY = useSpring(mouseY, springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);

      // Detect if we are hovering over a clickable element
      const target = e.target as HTMLElement;
      
      // Safety check: ensure target is an Element before accessing properties
      if (!target || typeof target.matches !== 'function') {
        setIsHovering(false);
        return;
      }

      // Explicitly ignore elements inside specific sections
      if (
        target.closest('header') || 
        target.closest('footer') ||
        target.closest('#contact-form') || 
        target.closest('#faq') || 
        target.closest('#contact') ||
        target.closest('.logo-strip') ||
        target.closest('#scroll-to-top') ||
        target.closest('#utility-icons')
      ) {
        setIsHovering(false);
        return;
      }

      const isClickable = 
        (target.tagName && (target.tagName.toLowerCase() === 'button' || target.tagName.toLowerCase() === 'a')) ||
        (target.closest && (target.closest('button') || target.closest('a'))) ||
        (target.classList && target.classList.contains('cursor-pointer')) ||
        (window.getComputedStyle(target).cursor === 'pointer');
      
      setIsHovering(!!isClickable);
    };

    const handleMouseDown = () => {
      setIsClicking(true);
      // Hide label on click
      setIsHovering(false);
    };
    
    const handleMouseUp = () => setIsClicking(false);

    const handleScroll = () => {
      // Hide label on scroll to prevent it sticking to the screen 
      // while elements move underneath
      setIsHovering(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [mouseX, mouseY]);

  const labelText = "LEARN MORE";

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] hidden md:block">
      {/* 
          Main anchor point tracking mouse coords. 
      */}
      <motion.div
        style={{
          x: cursorX,
          y: cursorY,
        }}
        className="absolute top-0 left-0"
      >
        {/* The Base Dot */}
        <motion.div
          animate={{
            width: isHovering ? 6 : 8,
            height: isHovering ? 6 : 8,
            borderRadius: isHovering ? 0 : '100%',
            backgroundColor: isHovering ? '#ff3b3b' : '#ffffff',
            scale: isClicking ? 0.7 : 1,
          }}
          className="absolute -translate-x-1/2 -translate-y-1/2 shadow-[0_0_15px_rgba(255,255,255,0.3)] dark:shadow-[0_0_15px_rgba(255,59,59,0.3)] z-20"
        />

        {/* Hover Label with Line */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{
            opacity: isHovering ? 1 : 0,
            scale: isHovering ? 1 : 0.5,
            x: isHovering ? 10 : 0, 
            y: isHovering ? 10 : 0,
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="absolute flex items-start"
        >
          {/* Connecting Line */}
          <div className="w-12 h-12 relative pointer-events-none overflow-visible">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" className="absolute top-0 left-0 overflow-visible">
              <motion.path
                d="M0,0 L40,40"
                stroke="#ff3b3b"
                strokeWidth={1.5}
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: isHovering ? 1 : 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </svg>
          </div>

          {/* Label Box */}
          <motion.div 
            animate={{ 
                x: isHovering ? 32 : 0, 
                y: isHovering ? 32 : 0 
            }}
            className="bg-[#0a0a0a] border border-brand-red/40 px-4 py-2 flex items-center gap-2 shadow-2xl backdrop-blur-md overflow-hidden"
          >
            <div className="flex items-center text-[10px] font-bold text-white dark:text-brand-red uppercase tracking-[0.2em] whitespace-nowrap">
              {labelText.split('').map((char, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, x: -2 }}
                  animate={{ 
                    opacity: isHovering ? 1 : 0,
                    x: isHovering ? 0 : -2
                  }}
                  transition={{ 
                    duration: 0.1,
                    delay: isHovering ? index * 0.04 + 0.1 : 0,
                    ease: "easeOut"
                  }}
                >
                  {char === " " ? "\u00A0" : char}
                </motion.span>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: isHovering ? 1 : 0,
                scale: isHovering ? 1 : 0
              }}
              transition={{ delay: isHovering ? 0.5 : 0, duration: 0.2 }}
            >
              <ArrowUpRight size={12} className="text-brand-red" />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CustomCursor;
